import { useEffect, useRef } from "react";
import { connectParityApi } from "@/api/connectApi";
import { useAccessToken } from "@/auth/useAccessToken";

// One-shot subscription: registers the `/sw.js` service worker (built into
// public/), fetches the server's VAPID key, asks the browser for Notification
// permission if not already granted, and posts the resulting PushSubscription
// to /api/push/subscribe. No-op when VAPID is disabled server-side, when the
// browser doesn't support push, or when the user denies the prompt — the
// app must work without push.

export function usePushSubscription() {
    const token = useAccessToken();
    const once = useRef(false);

    useEffect(() => {
        if (once.current) return;
        once.current = true;
        if (!token) { once.current = false; return; }
        void (async () => {
            try {
                if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
                const config = await connectParityApi.getPushConfig(token);
                if (!config.enabled || !config.key) return;
                if (Notification.permission === "denied") return;
                if (Notification.permission === "default") {
                    const perm = await Notification.requestPermission();
                    if (perm !== "granted") return;
                }

                const reg = await navigator.serviceWorker.register("/sw.js");
                await navigator.serviceWorker.ready;
                let sub = await reg.pushManager.getSubscription();
                if (!sub) {
                    sub = await reg.pushManager.subscribe({
                        userVisibleOnly: true,
                        applicationServerKey: urlBase64ToUint8Array(config.key) as Uint8Array<ArrayBuffer>
                    });
                }
                await connectParityApi.subscribePush(sub.toJSON(), navigator.userAgent, token);
            } catch {
                // Push is best-effort; never throw into the app.
            }
        })();
    }, [token]);
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = "=".repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = atob(base64);
    const output = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i);
    return output;
}
